import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Alert, Skeleton } from "antd"
import SharedAcceptedVirtualMeetCard, {
  ISharedAcceptedVirtualMeet,
  ISharedVirtualMeetParticipantOverview,
} from "./SharedAcceptedVirtualMeetCard"
import { gen, swimminglyApi } from "./utils"

const ACTIVITY_FEED_REFETCH_EVENT = "swimmingly:activity-feed:refetch"

export default function VirtualMeetResultsPublishedPreview({
  item,
}: {
  item: {
    clubId?: number | null
    context?: Record<string, any>
  }
}): JSX.Element | null {
  const clubId = Number(item.clubId || item.context?.clubId || 0)
  const virtualMeetInvitationId = Number(item.context?.virtualMeetInvitationId || 0)
  const virtualMeetId = Number(item.context?.virtualMeetId || 0)
  const [loading, setLoading] = useState(false)
  const [virtualMeet, setVirtualMeet] = useState<ISharedAcceptedVirtualMeet | null>(null)
  const [participants, setParticipants] = useState<ISharedVirtualMeetParticipantOverview[]>([])
  const [error, setError] = useState<string | null>(null)
  const [acceptedLegsExpanded, setAcceptedLegsExpanded] = useState(true)

  const missingIds = !clubId || (!virtualMeetInvitationId && !virtualMeetId)

  const loadPreview = useCallback(async () => {
    if (missingIds) {
      setVirtualMeet(null)
      setParticipants([])
      setError(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const virtualMeetResponse: any = await swimminglyApi.get(
        gen(`/api/getVisibleVirtualMeets?clubId=${clubId}`),
      )
      const visibleVirtualMeets: ISharedAcceptedVirtualMeet[] = Array.isArray(
        virtualMeetResponse?.virtualMeets,
      )
        ? virtualMeetResponse.virtualMeets
        : []

      const matchedVirtualMeet =
        visibleVirtualMeets.find(
          (candidate: any) =>
            candidate.status === "accepted" &&
            ((virtualMeetInvitationId > 0 &&
              Number(candidate.virtualMeetInvitationId) === virtualMeetInvitationId) ||
              (virtualMeetId > 0 && Number(candidate.virtualMeetId) === virtualMeetId)),
        ) || null

      const resolvedVirtualMeetId = Number(
        matchedVirtualMeet?.virtualMeetId || virtualMeetId || 0,
      )

      if (!resolvedVirtualMeetId) {
        throw new Error("Unable to load this virtual meet preview.")
      }

      const participantsResponse: any = await swimminglyApi.get(
        gen(`/api/getVirtualMeetParticipantsOverview/${resolvedVirtualMeetId}`),
      )
      const nextParticipants: ISharedVirtualMeetParticipantOverview[] = Array.isArray(
        participantsResponse?.participants,
      )
        ? participantsResponse.participants
        : []

      const fallbackVirtualMeet: ISharedAcceptedVirtualMeet = {
        virtualMeetInvitationId,
        virtualMeetId: resolvedVirtualMeetId,
        clubId,
        acceptedMeetId: Number(item.context?.meetId || 0) || null,
        meetName:
          String(item.context?.virtualMeetName || item.context?.meetName || "Virtual meet") ||
          "Virtual meet",
        meetdate: String(item.context?.meetDate || item.context?.meetdate || "") || "",
        startdate: item.context?.startdate || null,
        course: Number(item.context?.course || 0),
        organizerClubId: Number(participantsResponse?.organizerClubId || 0),
        organizerClubName: null,
        organizerClubNameLong: null,
        pendingInvitations: nextParticipants.filter((participant) => participant.status === "pending")
          .length,
        acceptedInvitations: nextParticipants.filter((participant) => participant.status === "accepted")
          .length,
        declinedInvitations: nextParticipants.filter((participant) => participant.status === "declined")
          .length,
        removedInvitations: nextParticipants.filter((participant) => participant.status === "removed")
          .length,
      }

      const resolvedVirtualMeet = matchedVirtualMeet || fallbackVirtualMeet

      setVirtualMeet(resolvedVirtualMeet)
      setParticipants(nextParticipants)
    } catch (err: any) {
      setVirtualMeet(null)
      setParticipants([])
      setError(err?.message || "Unable to load this virtual meet preview.")
    } finally {
      setLoading(false)
    }
  }, [clubId, item.context, missingIds, virtualMeetId, virtualMeetInvitationId])

  useEffect(() => {
    void loadPreview()
  }, [loadPreview])

  useEffect(() => {
    if (missingIds) {
      return
    }

    const handleRefresh = () => {
      void loadPreview()
    }

    window.addEventListener(ACTIVITY_FEED_REFETCH_EVENT, handleRefresh)

    return () => {
      window.removeEventListener(ACTIVITY_FEED_REFETCH_EVENT, handleRefresh)
    }
  }, [loadPreview, missingIds])

  const activeClubId = useMemo(() => (clubId > 0 ? clubId : null), [clubId])

  if (missingIds) {
    return (
      <Alert
        showIcon
        type="info"
        title="Open this virtual swim meet from the action button below."
      />
    )
  }

  if (loading) {
    return <Skeleton active paragraph={{ rows: 8 }} />
  }

  if (error) {
    return <Alert showIcon type="info" title={error} />
  }

  if (!virtualMeet) {
    return null
  }

  return (
    <SharedAcceptedVirtualMeetCard
      virtualMeet={virtualMeet}
      participants={participants}
      loadingParticipants={loading}
      activeClubId={activeClubId}
      acceptedLegsExpanded={acceptedLegsExpanded}
      onToggleAcceptedLegs={() => setAcceptedLegsExpanded((current) => !current)}
    />
  )
}